import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { IGenerateTask } from '@rendora/common/entities/youyan/generate_record_task';
import type { PaginationResponse } from '@rendora/common/types/base';
import { EXECUTION_STATUS } from '@rendora/common/types/enums';

type GenerateTaskFetcher = (
  params: Record<string, unknown>,
) => Promise<PaginationResponse<IGenerateTask>>;

interface PollingContext {
  fetcher: GenerateTaskFetcher;
  params: Record<string, unknown>;
  fetchStatuses: EXECUTION_STATUS[];
  continueStatuses: EXECUTION_STATUS[];
  interval: number;
  timer: NodeJS.Timeout | null;
  running: boolean;
  onDiff?: (tasks: IGenerateTask[]) => void;
  lastLength?: number;
}

type ApplyMode = 'replace' | 'append' | 'prepend';

type TaskSubscriber = (tasks: IGenerateTask[]) => void;

interface ApplyOptions {
  mode?: ApplyMode;
  startPolling?: boolean;
  fetcher?: GenerateTaskFetcher;
  params?: Record<string, unknown>;
  fetchStatuses?: EXECUTION_STATUS[];
  continueStatuses?: EXECUTION_STATUS[];
  interval?: number;
  onDiff?: (tasks: IGenerateTask[]) => void;
  immediate?: boolean;
}

interface StartPollingOptions {
  params?: Record<string, unknown>;
  fetchStatuses?: EXECUTION_STATUS[];
  continueStatuses?: EXECUTION_STATUS[];
  interval?: number;
  onDiff?: (tasks: IGenerateTask[]) => void;
  immediate?: boolean;
}

const DEFAULT_FETCH_STATUSES: EXECUTION_STATUS[] = [
  EXECUTION_STATUS.NOT_STARTED,
  EXECUTION_STATUS.DATA_INIT,
  EXECUTION_STATUS.WAITING,
  EXECUTION_STATUS.PROCESSING,
  EXECUTION_STATUS.ERROR,
];

const DEFAULT_CONTINUE_STATUSES: EXECUTION_STATUS[] = [
  EXECUTION_STATUS.NOT_STARTED,
  EXECUTION_STATUS.DATA_INIT,
  EXECUTION_STATUS.WAITING,
  EXECUTION_STATUS.PROCESSING,
];

const DEFAULT_POLLING_INTERVAL = 3000;

function deduplicateIds(ids: number[]): number[] {
  const seen = new Set<number>();
  const result: number[] = [];
  ids.forEach(id => {
    if (!seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  });
  return result;
}

function hasPendingTasks(tasks: IGenerateTask[], statuses: EXECUTION_STATUS[]): boolean {
  return tasks.some(task => statuses.includes(task.status));
}

export const useGenerateTaskStore = defineStore('generateTask', () => {
  const taskMap = ref<Record<number, IGenerateTask>>({});
  const scopeTaskIds = ref<Record<string, number[]>>({});

  const scopedTasks = computed<Record<string, IGenerateTask[]>>(() => {
    const result: Record<string, IGenerateTask[]> = {};
    const map = taskMap.value;
    Object.entries(scopeTaskIds.value).forEach(([scope, ids]) => {
      result[scope] = ids.map(id => map[id]).filter((task): task is IGenerateTask => Boolean(task));
    });
    return result;
  });

  const contexts: Record<string, PollingContext> = {};
  const subscribers: Record<string, Set<TaskSubscriber>> = {};

  function upsertTasks(tasks: IGenerateTask[]): void {
    if (tasks.length === 0) {
      return;
    }
    const nextMap: Record<number, IGenerateTask> = { ...taskMap.value };
    tasks.forEach(task => {
      nextMap[task.id] = {
        ...nextMap[task.id],
        ...task,
      };
    });
    taskMap.value = nextMap;
  }

  function updateScopeOrder(scope: string, ids: number[]): void {
    scopeTaskIds.value = {
      ...scopeTaskIds.value,
      [scope]: deduplicateIds(ids),
    };
  }

  function appendScopeOrder(scope: string, ids: number[]): void {
    const current = scopeTaskIds.value[scope] ?? [];
    const currentSet = new Set(current);
    const merged = [...current];
    ids.forEach(id => {
      if (!currentSet.has(id)) {
        merged.push(id);
      }
    });
    updateScopeOrder(scope, merged);
  }

  function prependScopeOrder(scope: string, ids: number[]): void {
    const current = scopeTaskIds.value[scope] ?? [];
    const currentSet = new Set(ids);
    const merged = [...ids, ...current.filter(id => !currentSet.has(id))];
    updateScopeOrder(scope, merged);
  }

  function notify(scope: string, tasks: IGenerateTask[]): void {
    const scopeSubscribers = subscribers[scope];
    if (!scopeSubscribers || scopeSubscribers.size === 0) {
      return;
    }
    scopeSubscribers.forEach(callback => {
      callback(tasks);
    });
  }

  function schedule(scope: string, delay: number): void {
    const context = contexts[scope];
    if (!context) {
      return;
    }
    if (context.timer) {
      clearTimeout(context.timer);
    }
    context.timer = setTimeout(() => {
      void executePoll(scope);
    }, delay);
  }

  async function executePoll(scope: string): Promise<void> {
    const context = contexts[scope];
    if (!context || context.running) {
      return;
    }
    context.running = true;
    try {
      const response = await context.fetcher({
        ...context.params,
        task_status: context.fetchStatuses.join(','),
      });
      const tasks = response.results;
      upsertTasks(tasks);
      notify(scope, tasks);
      if (
        context.onDiff &&
        context.lastLength !== undefined &&
        context.lastLength !== tasks.length
      ) {
        context.onDiff(tasks);
      }
      context.lastLength = tasks.length;
      if (hasPendingTasks(tasks, context.continueStatuses)) {
        schedule(scope, context.interval);
      } else {
        stopPolling(scope);
      }
    } catch (_error) {
      schedule(scope, context.interval);
    } finally {
      context.running = false;
    }
  }

  function notifyTaskUpdate(task: IGenerateTask): void {
    Object.entries(scopeTaskIds.value).forEach(([scope, ids]) => {
      if (ids.includes(task.id)) {
        notify(scope, [task]);
      }
    });
  }

  function subscribe(scope: string, callback: TaskSubscriber): () => void {
    if (!subscribers[scope]) {
      subscribers[scope] = new Set<TaskSubscriber>();
    }
    subscribers[scope].add(callback);
    return () => {
      subscribers[scope]?.delete(callback);
      if (subscribers[scope]?.size === 0) {
        delete subscribers[scope];
      }
    };
  }

  function startPolling(
    scope: string,
    fetcher: GenerateTaskFetcher,
    options?: StartPollingOptions,
  ): void {
    const params = options?.params ?? {};
    const fetchStatuses = options?.fetchStatuses ?? DEFAULT_FETCH_STATUSES;
    const continueStatuses = options?.continueStatuses ?? DEFAULT_CONTINUE_STATUSES;
    const interval = options?.interval ?? DEFAULT_POLLING_INTERVAL;
    const immediate = options?.immediate ?? true;

    stopPolling(scope);

    contexts[scope] = {
      fetcher,
      params,
      fetchStatuses,
      continueStatuses,
      interval,
      timer: null,
      running: false,
      onDiff: options?.onDiff,
      lastLength: undefined,
    };

    if (immediate) {
      void executePoll(scope);
    } else {
      schedule(scope, interval);
    }
  }

  function stopPolling(scope: string): void {
    const context = contexts[scope];
    if (context?.timer) {
      clearTimeout(context.timer);
    }
    if (contexts[scope]) {
      delete contexts[scope];
    }
  }

  function updatePollingParams(scope: string, params: Record<string, unknown>): void {
    const context = contexts[scope];
    if (context) {
      context.params = params;
    }
  }

  async function triggerPoll(scope: string): Promise<void> {
    await executePoll(scope);
  }

  function removeFromScope(scope: string, ids: number[]): void {
    if (ids.length === 0) {
      return;
    }
    const current = scopeTaskIds.value[scope];
    if (!current) {
      return;
    }
    updateScopeOrder(
      scope,
      current.filter(id => !ids.includes(id)),
    );
  }

  function clearScope(scope: string): void {
    stopPolling(scope);
    const nextScopeIds = { ...scopeTaskIds.value };
    delete nextScopeIds[scope];
    scopeTaskIds.value = nextScopeIds;
    if (subscribers[scope]) {
      delete subscribers[scope];
    }
  }

  function applyTaskList(scope: string, tasks: IGenerateTask[], options?: ApplyOptions): void {
    const mode = options?.mode ?? 'replace';
    if (mode === 'replace') {
      updateScopeOrder(
        scope,
        tasks.map(task => task.id),
      );
    } else if (mode === 'append') {
      appendScopeOrder(
        scope,
        tasks.map(task => task.id),
      );
    } else {
      prependScopeOrder(
        scope,
        tasks.map(task => task.id),
      );
    }

    upsertTasks(tasks);
    notify(scope, tasks);

    if (options?.startPolling && options.fetcher) {
      if (hasPendingTasks(tasks, options.continueStatuses ?? DEFAULT_CONTINUE_STATUSES)) {
        startPolling(scope, options.fetcher, {
          params: options.params,
          fetchStatuses: options.fetchStatuses,
          continueStatuses: options.continueStatuses,
          interval: options.interval,
          onDiff: options.onDiff,
          immediate: options.immediate,
        });
      } else {
        stopPolling(scope);
      }
    }
  }

  function getScopedTasks(scope: string): IGenerateTask[] {
    return scopedTasks.value[scope] ?? [];
  }

  function getScopeSize(scope: string): number {
    return scopeTaskIds.value[scope]?.length ?? 0;
  }

  function updateTask(task: IGenerateTask): void {
    upsertTasks([task]);
    notifyTaskUpdate(task);
  }

  return {
    applyTaskList,
    clearScope,
    getScopedTasks,
    getScopeSize,
    removeFromScope,
    startPolling,
    stopPolling,
    subscribe,
    triggerPoll,
    updatePollingParams,
    updateTask,
  };
});
